import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  mergePdfWithTemplate,
  mergePdfWithTemplateBundle,
  mergePdfWithAlgemeneVoorwaarde,
  downloadPdf
} from '../../utils/pdfUtils';
import { ExportOptions } from '../../types';

export function Export() {
  const { state, dispatch } = useApp();
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    quality: 'high',
    filename: undefined
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!state.currentDocument) {
      dispatch({ type: 'SET_ERROR', payload: 'Geen document geladen om te exporteren' });
      return;
    }

    setIsExporting(true);
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      let finalPdfData: ArrayBuffer;

      // Check if we need to include Algemene Voorwaarde
      if (state.algemeneVoorwaarde && state.includeAlgemeneVoorwaarde) {
        finalPdfData = await mergePdfWithAlgemeneVoorwaarde(
          state.currentDocument.pdfData,
          state.selectedTemplate?.pdfData || null,
          state.algemeneVoorwaarde.pdfData,
          state.templateOpacity,
          state.documentOpacity,
          state.ignoreWhiteBackground,
          state.selectedTemplate || undefined,
          state.pageTemplateMappings
        );
      } else if (state.selectedTemplate) {
        // Check if it's a bundle
        if (state.selectedTemplate.isBundle && state.selectedTemplate.pages) {
          finalPdfData = await mergePdfWithTemplateBundle(
            state.currentDocument.pdfData,
            state.selectedTemplate,
            state.pageTemplateMappings
          );
        } else {
          finalPdfData = await mergePdfWithTemplate(
            state.currentDocument.pdfData,
            state.selectedTemplate.pdfData,
            state.templateOpacity,
            state.documentOpacity,
            state.ignoreWhiteBackground
          );
        }
      } else {
        // If no template, but document opacity is not 100%, we still need to process it
        if (state.documentOpacity < 1.0) {
          // For now, if there's no template but document opacity is less than 100%,
          // we'll export the original document (PDF-lib can't easily adjust document opacity alone)
          finalPdfData = state.currentDocument.pdfData;
        } else {
          finalPdfData = state.currentDocument.pdfData;
        }
      }

      const filename = exportOptions.filename ||
        `${state.currentDocument.name.replace('.pdf', '')}${state.selectedTemplate ? `_${state.selectedTemplate.name}` : '_origineel'}${state.algemeneVoorwaarde && state.includeAlgemeneVoorwaarde ? '_met_voorwaarden' : ''}.pdf`;

      downloadPdf(finalPdfData, filename);

      dispatch({ type: 'CLEAR_ERROR' });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Fout bij exporteren' 
      });
    } finally {
      setIsExporting(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const canExport = state.currentDocument && !state.isLoading;

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Export</h2>
      
      <div className="space-y-6">
        <div className="bg-white dark:bg-black border border-gray-200 dark:border-blue-600 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Export Opties</h3>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="quality" className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Kwaliteit
              </label>
              <select
                id="quality"
                value={exportOptions.quality}
                onChange={(e) => setExportOptions({ 
                  ...exportOptions, 
                  quality: e.target.value as ExportOptions['quality'] 
                })}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-blue-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-black text-gray-900 dark:text-white"
              >
                <option value="high">Hoge kwaliteit (aanbevolen voor printen)</option>
                <option value="medium">Gemiddelde kwaliteit</option>
                <option value="low">Lage kwaliteit (kleiner bestand)</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-2">
                Bestandsnaam (optioneel)
              </label>
              <input
                id="filename"
                type="text"
                placeholder={`${state.currentDocument?.name.replace('.pdf', '') || 'document'}_export.pdf`}
                value={exportOptions.filename || ''}
                onChange={(e) => setExportOptions({ 
                  ...exportOptions, 
                  filename: e.target.value 
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-black border border-gray-200 dark:border-blue-600 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Samenvatting</h3>
          
          <div className="space-y-2 text-sm text-gray-600 dark:text-white">
            <div className="flex justify-between">
              <span>Document:</span>
              <span className="font-medium">
                {state.currentDocument ? state.currentDocument.name : 'Geen document'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Template:</span>
              <span className="font-medium">
                {state.selectedTemplate ? state.selectedTemplate.name : 'Geen template'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Pagina's:</span>
              <span className="font-medium">
                {state.currentDocument ?
                  state.currentDocument.pageCount +
                  (state.algemeneVoorwaarde && state.includeAlgemeneVoorwaarde ?
                    state.algemeneVoorwaarde.pageCount : 0) : 0}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Kwaliteit:</span>
              <span className="font-medium capitalize">
                {exportOptions.quality === 'high' ? 'Hoog' :
                 exportOptions.quality === 'medium' ? 'Gemiddeld' : 'Laag'}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Algemene Voorwaarde:</span>
              <span className="font-medium">
                {state.algemeneVoorwaarde && state.includeAlgemeneVoorwaarde ?
                  state.algemeneVoorwaarde.name :
                  'Niet toegevoegd'}
              </span>
            </div>

            {/* Transparency warning temporarily disabled */}
            {false && state.selectedTemplate && state.ignoreWhiteBackground && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-yellow-700">
                  ⚠️ Let op: "Wit negeren" werkt alleen in preview. 
                  In de geëxporteerde PDF wordt de volledige template toegepast.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleExport}
            disabled={!canExport || isExporting}
            className={`
              px-6 py-3 rounded-lg font-medium transition-colors
              ${canExport && !isExporting
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {isExporting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporteren...
              </span>
            ) : (
              'PDF Downloaden'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}